import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";

import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import MobileStickyCTA from "@/components/MobileStickyCTA";

import { GALLERY_CATEGORIES, GALLERY_ITEMS } from "@/lib/gallery-data";

const HERO_BG =
    "https://customer-assets.emergentagent.com/job_caribbean-premium/artifacts/268snciq_4B00982C-BDB8-4044-BB1B-7B7FE2243CA8.PNG";

// ---------------- Hero ----------------
function Hero() {
    return (
        <section
            data-testid="gallery-hero"
            className="relative min-h-[78svh] md:min-h-[88svh] overflow-hidden bg-charcoal"
            aria-label="Gallery hero"
        >
            <div
                aria-hidden
                className="absolute inset-0 bg-cover bg-no-repeat"
                style={{ backgroundImage: `url(${HERO_BG})`, backgroundPosition: "center 50%" }}
            />
            <div className="absolute inset-0 bg-charcoal/55" />
            <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/70 to-charcoal/20" />
            <div className="absolute inset-0 bg-gradient-to-b from-charcoal/40 via-transparent to-charcoal/90" />

            <div className="relative z-10 empire-container min-h-[78svh] md:min-h-[88svh] flex flex-col justify-end lg:justify-center pt-40 pb-20 lg:py-0">
                <div className="w-full lg:max-w-[60%]">
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="empire-eyebrow"
                    >
                        <span className="inline-block h-px w-8 bg-gold align-middle mr-3" />
                        In the Room
                    </motion.p>

                    <motion.h1
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                        data-testid="gallery-headline"
                        className="mt-7 font-serif text-cream text-[3rem] leading-[1.02] tracking-[-0.01em] md:text-[5rem] lg:text-[6rem]"
                    >
                        Gallery<span className="text-gold">.</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.32 }}
                        className="mt-7 max-w-xl text-cream/80 text-[15px] md:text-[17px] font-light leading-[1.75]"
                    >
                        A look inside the food, the atmosphere, and the moments that make
                        Empire feel like home.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                    >
                        <a href="/catering" data-testid="gallery-hero-book" className="empire-btn-primary">
                            Book Catering
                            <ArrowRight size={14} />
                        </a>
                        <a href="/menu" data-testid="gallery-hero-menu" className="empire-btn-secondary">
                            View Menu
                        </a>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

// ---------------- Grid ----------------
function GridSection() {
    const [active, setActive] = useState("all");

    const items = useMemo(
        () =>
            active === "all"
                ? GALLERY_ITEMS
                : GALLERY_ITEMS.filter((g) => g.category === active),
        [active]
    );

    return (
        <section
            data-testid="gallery-grid-section"
            className="relative bg-charcoal"
            aria-label="Gallery grid"
        >
            <div className="empire-container py-16 md:py-24">
                {/* Filters */}
                <div
                    data-testid="gallery-filters"
                    className="flex flex-wrap items-center gap-x-6 gap-y-3 md:gap-x-9 border-b border-cream/[0.08] pb-6 mb-10 md:mb-14"
                >
                    {GALLERY_CATEGORIES.map((c) => {
                        const isActive = active === c.id;
                        return (
                            <button
                                key={c.id}
                                type="button"
                                onClick={() => setActive(c.id)}
                                data-testid={`gallery-filter-${c.id}`}
                                aria-pressed={isActive}
                                className={`relative text-[11px] md:text-[12px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                                    isActive ? "text-gold" : "text-cream/55 hover:text-cream"
                                }`}
                            >
                                {c.label}
                                <span
                                    className={`absolute -bottom-[25px] left-0 h-px bg-gold transition-all duration-300 ${
                                        isActive ? "w-full" : "w-0"
                                    }`}
                                />
                            </button>
                        );
                    })}
                </div>

                {/* Grid */}
                <motion.div
                    layout
                    className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5"
                >
                    <AnimatePresence mode="popLayout">
                        {items.map((g, i) => (
                            <motion.figure
                                layout
                                key={g.src}
                                initial={{ opacity: 0, y: 22 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.97 }}
                                transition={{
                                    duration: 0.6,
                                    delay: (i % 6) * 0.05,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                data-testid={`gallery-item-${g.category}`}
                                className="group relative overflow-hidden bg-charcoal-800 aspect-[4/5] border border-cream/[0.06] hover:border-gold/40 transition-colors duration-500"
                            >
                                <img
                                    src={g.src}
                                    alt={`${g.title} — Empire Jamaica Fusion`}
                                    loading="lazy"
                                    decoding="async"
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1300ms] ease-out group-hover:scale-[1.07]"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/10 to-transparent opacity-80 group-hover:opacity-95 transition-opacity duration-500" />
                                <div className="absolute inset-0 ring-0 group-hover:ring-1 group-hover:ring-gold/30 ring-inset transition-all duration-500" />
                                <figcaption className="absolute left-3 right-3 bottom-3 md:left-5 md:right-5 md:bottom-5 translate-y-1 group-hover:translate-y-0 transition-transform duration-500">
                                    <div className="text-gold text-[8.5px] md:text-[10px] uppercase tracking-[0.24em]">
                                        {g.caption}
                                    </div>
                                    <div className="mt-1 font-serif text-cream text-[0.95rem] md:text-[1.2rem] leading-tight tracking-tight">
                                        {g.title}
                                    </div>
                                </figcaption>
                            </motion.figure>
                        ))}
                    </AnimatePresence>
                </motion.div>
            </div>
        </section>
    );
}

// ---------------- Closing CTA ----------------
function ClosingCTA() {
    return (
        <section
            data-testid="gallery-cta-section"
            className="relative overflow-hidden bg-burgundy"
            aria-label="Bring Empire to your table"
        >
            <div className="absolute inset-0 z-0">
                <img
                    src="https://customer-assets.emergentagent.com/job_b8a7810c-24f2-4266-91d4-0b33b2eb0b66/artifacts/so154t3e_4D544B4D-5ED1-4118-B68A-2CABF55656D8%202.PNG"
                    alt=""
                    aria-hidden
                    className="absolute inset-0 w-full h-full object-cover opacity-25"
                />
                <div className="absolute inset-0 bg-burgundy/80" />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-burgundy/70 to-charcoal/40" />
            </div>

            <div className="relative z-10 empire-container py-24 md:py-36 text-center">
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="empire-eyebrow !text-gold"
                >
                    Catering & Private Events
                </motion.p>
                <motion.h2
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                    data-testid="gallery-cta-heading"
                    className="mt-6 mx-auto max-w-4xl font-serif text-cream text-[2.2rem] md:text-[3.6rem] lg:text-[4.4rem] leading-[1.05] tracking-[-0.01em]"
                >
                    Ready to bring Empire to<br className="hidden md:block" /> your table?
                </motion.h2>
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="mt-6 max-w-xl mx-auto text-cream/80 text-base font-light"
                >
                    From intimate dinners to full-service catering, we&rsquo;ll help create a
                    gathering your guests remember.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.45 }}
                    className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
                >
                    <a
                        href="/catering"
                        data-testid="gallery-cta-book-catering"
                        className="inline-flex items-center justify-center gap-2 bg-cream text-charcoal px-8 py-4 text-[12px] uppercase tracking-[0.22em] font-medium hover:bg-gold transition-colors duration-300"
                    >
                        Book Catering
                        <ArrowRight size={14} />
                    </a>
                    <a
                        href="/private-events"
                        data-testid="gallery-cta-private-event"
                        className="inline-flex items-center justify-center gap-2 border border-cream/70 text-cream px-8 py-4 text-[12px] uppercase tracking-[0.22em] font-medium hover:bg-cream hover:text-charcoal transition-colors duration-300"
                    >
                        Plan a Private Event
                    </a>
                </motion.div>
            </div>
        </section>
    );
}

// ---------------- Page ----------------
export default function Gallery() {
    useEffect(() => {
        document.title = "Gallery · Empire Jamaica Fusion · Poughkeepsie, NY";
        window.scrollTo({ top: 0, behavior: "auto" });
    }, []);

    return (
        <>
            <Nav />
            <main data-testid="gallery-page">
                <Hero />
                <GridSection />
                <ClosingCTA />
            </main>
            <Footer />
            <MobileStickyCTA />
        </>
    );
}
